import type { CuePoints, TransitionProfile } from './types';

export type CueField = 'cueInMs' | 'introEndMs' | 'vocalStartMs' | 'outroStartMs' | 'cueOutMs';
export type UploadBatchStatus = 'QUEUED' | 'UPLOADING' | 'ANALYZING' | 'READY' | 'DUPLICATE' | 'FAILED';

export interface UploadBatchSummary {
  total: number;
  finished: number;
  succeeded: number;
  failed: number;
  percent: number;
}

export function summarizeUploadBatch(statuses: readonly UploadBatchStatus[]): UploadBatchSummary {
  const succeeded = statuses.filter((status) => status === 'READY' || status === 'DUPLICATE').length;
  const failed = statuses.filter((status) => status === 'FAILED').length;
  const finished = succeeded + failed;
  return {
    total: statuses.length,
    finished,
    succeeded,
    failed,
    percent: statuses.length === 0 ? 0 : Math.round((finished / statuses.length) * 100),
  };
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

export async function mapWithConcurrency<T, R>(items: readonly T[], concurrency: number, worker: (item: T, index: number) => Promise<R>): Promise<R[]> {
  if (!Number.isInteger(concurrency) || concurrency < 1) throw new Error('Concurrency must be a positive integer.');
  const results = new Array<R>(items.length);
  let nextIndex = 0;
  async function consume(): Promise<void> {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await worker(items[index]!, index);
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, () => consume()));
  return results;
}

export function updateCueMarker(cues: CuePoints, field: CueField, value: number, durationMs: number): CuePoints {
  const next = { ...cues, [field]: Math.round(Math.max(0, Math.min(durationMs, value))) };
  const ordered: Array<[CueField, number | null]> = [
    ['cueInMs', next.cueInMs],
    ['introEndMs', next.introEndMs],
    ['vocalStartMs', next.vocalStartMs],
    ['outroStartMs', next.outroStartMs],
    ['cueOutMs', next.cueOutMs],
  ];
  const index = ordered.findIndex(([name]) => name === field);
  const previous = [...ordered.slice(0, index)].reverse().find(([, marker]) => marker !== null)?.[1] ?? 0;
  const following = ordered.slice(index + 1).find(([, marker]) => marker !== null)?.[1] ?? durationMs;
  next[field] = Math.max(previous, Math.min(following, next[field] as number));
  if (next.cueOutMs <= next.cueInMs) {
    next.cueOutMs = Math.min(durationMs, next.cueInMs + 1);
  }
  const playable = next.cueOutMs - next.cueInMs;
  next.fadeInMs = Math.min(next.fadeInMs, playable);
  next.fadeOutMs = Math.min(next.fadeOutMs, playable);
  return next;
}

export function effectiveOverlap(profile: TransitionProfile | null, currentDurationMs: number, nextDurationMs: number, nextCues: CuePoints): number {
  if (profile === null) return 0;
  let overlap = Math.min(profile.settings.overlapMs, Math.max(0, currentDurationMs - 1), Math.max(0, nextDurationMs - 1));
  if (profile.mode === 'OVERLAY') {
    overlap = nextCues.vocalStartMs === null ? 0 : Math.min(overlap, Math.max(0, nextCues.vocalStartMs - nextCues.cueInMs));
  }
  return overlap;
}

export function formatDuration(milliseconds: number): string {
  const normalized = Math.max(0, Math.round(milliseconds));
  const minutes = Math.floor(normalized / 60_000);
  const seconds = Math.floor((normalized % 60_000) / 1_000);
  const millis = normalized % 1_000;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
}

export function parseTimecode(value: string, maximumMs: number): number | null {
  const normalized = value.trim().replace(',', '.');
  const timecode = /^(\d+):([0-5]?\d)(?:\.(\d{1,3}))?$/.exec(normalized);
  if (timecode !== null) {
    const minutes = Number(timecode[1]);
    const seconds = Number(timecode[2]);
    const milliseconds = Number((timecode[3] ?? '').padEnd(3, '0'));
    return Math.min(maximumMs, minutes * 60_000 + seconds * 1_000 + milliseconds);
  }

  if (/^\d+(?:\.\d{1,3})?$/.test(normalized)) {
    return Math.min(maximumMs, Math.round(Number(normalized) * 1_000));
  }

  return null;
}

export function playbackPercent(milliseconds: number, durationMs: number): number {
  if (durationMs <= 0) return 0;
  return Math.max(0, Math.min(100, (milliseconds / durationMs) * 100));
}

export function trailingSilenceDuration(durationMs: number, silenceEndMs: number | null): number {
  return Math.max(0, durationMs - (silenceEndMs ?? durationMs));
}
